import {
  CameraOptions,
  CameraType,
  PointcloudDisplay,
  VizScene,
  RangeRGBData,
  DataMessage,
  resolveHoveredPoint,
} from '@ouster/webviz';
import {
  Box,
  Flex,
  Loader,
  Grid,
  chakra,
  TwoDImagePanel,
  PointCloudGlobalSettings,
  PointCloudSensorsSettings,
  ImageContainerState,
  PointCloudHoverTooltip,
} from '@ouster/webviz-react-components';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { useStoreState, useStoreActions } from '../reducers';
import { DriveInfo, HoveredPointData } from '../types';

// Set the initial position and type of the camera
const DEFAULT_CAMERA_OPTIONS: CameraOptions = {
  cameraType: CameraType.PERSP,
  position: [20, 20, 10],
};

type VizPageProps = {
  driveInfo: DriveInfo;
  pointSize: number;
  flipped: boolean;
  pointCloudGlobalSettings: PointCloudGlobalSettings;
  pointCloudSensorsSettings: PointCloudSensorsSettings;
  paused: boolean;
  hoverMode: boolean;
  setHoverMode: (mode: boolean) => void;
  onIsDrawnChange?: (isDrawn: boolean) => void;
};

export interface VizPageRef {
  setSeekingState: (seeking: boolean) => void;
  getIsDrawn: () => boolean;
}

/**
 * Main visualizer component
 * @param props - Chakra UI style props
 * panel
 * @returns div containing viz canvas
 */
export const VizPage = forwardRef<VizPageRef, VizPageProps>((props, ref) => {
  const {
    driveInfo,
    pointSize,
    flipped,
    pointCloudGlobalSettings,
    pointCloudSensorsSettings,
    paused,
    hoverMode,
    setHoverMode,
    onIsDrawnChange,
  } = props;

  const threeDSceneCanvasRef = useRef<HTMLCanvasElement>(null);
  const threeDSceneDivRef = useRef<HTMLDivElement>(null);
  const [vizScene, setVizScene] = useState<VizScene>();
  const [pcDisplay, setPcDisplay] = useState<PointcloudDisplay>();
  const [isDrawn, setIsDrawn] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [scaledOffset, setScaledOffset] = useState([0, 0]);
  const [containerState, setContainerState] = useState<
    ImageContainerState | undefined
  >(undefined);
  const [hoveredPointData, setHoveredPointData] =
    useState<HoveredPointData | null>(null);
  const [currentDataMessage, setCurrentDataMessage] = useState<
    DataMessage[] | null
  >(null);
  const isPointerDownRef = useRef(false);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const isSeekingRef = useRef(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isZooming, setIsZooming] = useState(false);
  const zoomTimeoutRef = useRef<NodeJS.Timeout>();
  const lastZoomRef = useRef<number>(1);
  const lastDistanceRef = useRef<number>(0);
  const hoverModeRef = useRef(hoverMode);
  const pcDisplayRef = useRef(pcDisplay);
  const isZoomingRef = useRef(false);

  useEffect(() => {
    if (onIsDrawnChange) onIsDrawnChange(isDrawn);
  }, [isDrawn, onIsDrawnChange]);

  const {
    pointCloudObservable,
    is2DImageVisible,
    twoDImageSettings,
    dataMessage,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    metadata,
  } = useStoreState((store) => ({
    ...store.vizScene,
  }));
  const { setIs2DImageVisible, initAvailableChannels } = useStoreActions(
    (actions) => ({
      setIs2DImageVisible: actions.vizScene.setIs2DImageVisible,
      initAvailableChannels: actions.vizScene.initAvailableChannels,
    })
  );

  const { coloringMode, showPointCloud, isOrthographicCamera } =
    pointCloudGlobalSettings;
  const {
    showImageRulers,
    imageInfo,
    imageObservables,
    selectedImagePanelSn,
    imageSettings,
  } = twoDImageSettings;

  const selectedImageData = useMemo(() => {
    // Ensure we have a selected sensor, its info, and its observables
    if (
      !selectedImagePanelSn ||
      !imageInfo || // Check if imageInfo itself exists
      !imageInfo[selectedImagePanelSn] ||
      !imageObservables ||
      imageObservables.length === 0 ||
      !imageSettings[selectedImagePanelSn]
    ) {
      return null; // Return null if any required data is missing
    }
    // The imageObservables array in the state *already* contains only the observables for the selected sensor
    return {
      sensorName: selectedImagePanelSn,
      info: imageInfo[selectedImagePanelSn],
      observables: imageObservables,
    };
  }, [selectedImagePanelSn, imageInfo, imageObservables, imageSettings]);

  const sensorSerialNumbers = useMemo(() => {
    // Use metadata from the store instead of driveInfo.sensorInfos
    // the driveInfo is not always correct with the sensorInfos. It is more reliable to use the metadata
    // which we got from recording
    if (Array.isArray(metadata) && metadata.length > 0) {
      return (metadata as Array<{ productSn?: string }>).reduce<string[]>(
        (acc, sensorMeta) => {
          if (typeof sensorMeta.productSn === 'string') {
            acc.push(sensorMeta.productSn);
          }
          return acc;
        },
        []
      );
    }
    // Fallback to driveInfo if metadata not available yet
    return (
      driveInfo?.sensorInfos?.map((info) => info.sdkProductSerialNumber) ?? []
    );
  }, [metadata, driveInfo]);

  const whichReturnNumbers = useMemo(() => {
    const allVisibleIndices: number[] = [];
    if (!pointCloudSensorsSettings || sensorSerialNumbers.length === 0) {
      return [];
    }

    sensorSerialNumbers.forEach((sn) => {
      if (!sn) return;
      const sensorSettings = pointCloudSensorsSettings[sn];
      if (sensorSettings?.returnsSettings) {
        // For each return of this sensor, add to global array if visible
        sensorSettings.returnsSettings.forEach((returnSetting, returnIndex) => {
          if (returnSetting?.visible) {
            allVisibleIndices.push(returnIndex);
          }
        });
      }
    });

    return allVisibleIndices;
  }, [sensorSerialNumbers, pointCloudSensorsSettings]);

  // This UseEffect runs once, on startup
  // When pc div is available, create the viz scene and register event listeners
  useEffect(() => {
    if (!vizScene) {
      if (!threeDSceneCanvasRef.current || !threeDSceneDivRef.current) {
        throw new Error(`Unable to find canvas for pointcloud!`);
      }

      const scene = new VizScene(
        threeDSceneCanvasRef.current,
        DEFAULT_CAMERA_OPTIONS,
        threeDSceneDivRef.current
      );
      const pc = new PointcloudDisplay(scene, coloringMode);
      setPcDisplay(pc);
      setVizScene(scene);
    }
  }, [coloringMode, vizScene]);

  /* 
    Due to the compete, the point size is always set up with the proper value during drawing.
    we use this ref to make it when the canvas first get drawn, the point size is correct.
    So there will be no flickering of point size changing during loading. -- point size init moved to subscription
  */
  const didInitChannelsRef = useRef(false);

  // Subscription to pc observable
  // We need to resubscribe any time the pointcloud needs rerendering from a setting change
  useEffect(() => {
    const pointsSubscription = pointCloudObservable.subscribe(
      (message: RangeRGBData[][]) => {
        if (!pcDisplay || !vizScene || !message) return;

        // Batch all mutations affecting gpu inside subscription
        if (pcDisplay.pointSize !== pointSize) {
          pcDisplay.pointSize = pointSize; // applies before the very first draw as well
        }
        if (pcDisplay.isVisible !== showPointCloud) {
          pcDisplay.isVisible = showPointCloud;
        }
        pcDisplay.whichReturns = whichReturnNumbers;
        pcDisplay.channelField = coloringMode;

        if (!didInitChannelsRef.current) {
          initAvailableChannels();
          didInitChannelsRef.current = true;
        }

        if (!isDrawn) setIsDrawn(true);

        pcDisplay.draw(message);
      }
    );
    return () => {
      pointsSubscription.unsubscribe();
    };
  }, [
    pointCloudSensorsSettings,
    pcDisplay,
    pointCloudObservable,
    vizScene,
    whichReturnNumbers,
    pointCloudGlobalSettings.coloringMode,
    pointCloudGlobalSettings.colorPalette,
    pointCloudGlobalSettings.isAutoLevelsOn,
    pointCloudGlobalSettings.isNoiseSuppressionOn,
    pointCloudGlobalSettings.pointSize,
    isDrawn,
    showPointCloud,
    initAvailableChannels,
    coloringMode,
    pointSize,
  ]);

  useEffect(() => {
    const subscription = dataMessage.subscribe((messages) => {
      setCurrentDataMessage(messages);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dataMessage]);

  useEffect(() => {
    if (!vizScene || !pcDisplay) {
      return () => {};
    }
    const handleResize = (): void => {
      pcDisplay.resizePickingFramebuffer();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [vizScene, pcDisplay]);

  // Listen for pointcloud flip event
  useEffect(() => {
    if (!pcDisplay) return;
    if (flipped) {
      pcDisplay.extrinsics = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 1];
    } else {
      pcDisplay.extrinsics = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
    }
  }, [flipped, pcDisplay]);

  useEffect(() => {
    if (!vizScene) return;
    // Reset zoom tracking references when camera type changes
    /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
    lastDistanceRef.current = vizScene.camera.position.distanceTo(
      vizScene.controls.target
    );
    /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
    lastZoomRef.current = 'zoom' in vizScene.camera ? vizScene.camera.zoom : 1;

    vizScene.setCameraType(isOrthographicCamera);
  }, [isOrthographicCamera, vizScene]);

  useEffect(() => {
    if (!vizScene) return;
    const { showRangeRings } = pointCloudGlobalSettings;
    vizScene.setRangeRingsVisibility(showRangeRings);
  }, [pointCloudGlobalSettings, vizScene]);

  useEffect(() => {
    if (!paused) {
      setHoverMode(false);
      setHoveredPointData(null);
      if (pcDisplay) {
        pcDisplay.setHoverActive(false);
      }
    }
  }, [paused, hoverMode, setHoverMode, pcDisplay]);

  useEffect(() => {
    if (
      !vizScene ||
      !hoverMode ||
      !pcDisplay ||
      isPointerDown ||
      isSeeking ||
      isZooming
    )
      return () => {};
    if (vizScene) {
      vizScene.setHoverActive(hoverMode);
    }
    if (pcDisplay) {
      pcDisplay.setHoverActive(hoverMode);
    }
    const handleMouseMove = (event: MouseEvent) => {
      // skip hover if dragging
      if (
        isPointerDownRef.current ||
        isSeeking ||
        isZooming ||
        event.buttons !== 0
      ) {
        setHoveredPointData(null);
        return;
      }
      const { clientX, clientY } = event;

      if (currentDataMessage) {
        const hoveredPoint = resolveHoveredPoint(
          vizScene,
          pcDisplay,
          currentDataMessage,
          clientX,
          clientY,
          {
            hoverMode,
            isPointerDown: isPointerDownRef.current,
            overPanelContainerId: 'container-image2d',
          }
        );

        setHoveredPointData(hoveredPoint);
      } else {
        setHoveredPointData(null);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [
    vizScene,
    hoverMode,
    currentDataMessage,
    hoveredPointData,
    pcDisplay,
    isPointerDown,
    isSeeking,
    isZooming,
  ]);
  useEffect(() => {
    // Clear hover tooltip when frame changes
    if (hoverMode) {
      setHoveredPointData(null);
      if (pcDisplay) {
        pcDisplay.setHoverActive(false);
      }
    }
  }, [currentDataMessage, pcDisplay, hoverMode]);

  useEffect(() => {
    hoverModeRef.current = hoverMode;
  }, [hoverMode]);
  useEffect(() => {
    pcDisplayRef.current = pcDisplay || undefined;
  }, [pcDisplay]);
  useEffect(() => {
    isZoomingRef.current = isZooming;
  }, [isZooming]);

  /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
  useEffect(() => {
    if (!vizScene) return () => {};

    lastDistanceRef.current = vizScene.camera.position.distanceTo(
      vizScene.controls.target
    );
    lastZoomRef.current = 'zoom' in vizScene.camera ? vizScene.camera.zoom : 1;
    const handleControlsChange = () => {
      const { camera, controls } = vizScene;
      if (!camera || !controls) return;
      let isZoomChange = false;
      // For perspective camera: detect distance changes (dollying)
      if (camera.isPerspectiveCamera) {
        const currentDistance = camera.position.distanceTo(controls.target);
        const distanceChange = Math.abs(
          currentDistance - lastDistanceRef.current
        );
        if (distanceChange > 0.01) {
          isZoomChange = true;
          lastDistanceRef.current = currentDistance;
        }
        // For orthographic camera: detect zoom property changes
      } else if (camera.isOrthographicCamera) {
        const currentZoom = camera.zoom;
        const zoomChange = Math.abs(currentZoom - lastZoomRef.current);
        if (zoomChange > 0.01) {
          isZoomChange = true;
          lastZoomRef.current = currentZoom;
        }
      }
      if (!isZoomChange) return;
      // trailing debounce keep pushing the end time forward
      if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current);
      // suspend hover once
      if (!isZoomingRef.current) {
        isZoomingRef.current = true;
        setIsZooming(true);
        pcDisplayRef.current?.setHoverActive(false);
        setHoveredPointData(null);
      }
      zoomTimeoutRef.current = setTimeout(() => {
        isZoomingRef.current = false;
        setIsZooming(false);
        if (hoverModeRef.current) {
          pcDisplayRef.current?.setHoverActive(true);
        }
      }, 150);
    };
    vizScene.controls.addEventListener('change', handleControlsChange);
    return () => {
      vizScene.controls.removeEventListener('change', handleControlsChange);
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current); // only when scene changes/unmounts
        zoomTimeoutRef.current = undefined;
      }
    };
  }, [vizScene]);
  /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
  useEffect(() => {
    if (!vizScene || !pcDisplay) return () => {};
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const canvasEl = vizScene.renderer.domElement as HTMLCanvasElement;
    const onPointerDown = () => {
      isPointerDownRef.current = true;
      setIsPointerDown(true);
      // Turn off hover while dragging to avoid flicker and expensive readbacks
      pcDisplay.setHoverActive(false);
      setHoveredPointData(null); // hide tooltip immediately
    };
    const onPointerUpOrCancel = () => {
      isPointerDownRef.current = false;
      setIsPointerDown(false);
      // Re-enable hover and rebuild picking framebuffer with the new camera view
      if (hoverMode) {
        pcDisplay.setHoverActive(true); // this calls renderPickingFramebuffer() internally
      }
    };
    canvasEl.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUpOrCancel);
    window.addEventListener('pointercancel', onPointerUpOrCancel);
    window.addEventListener('pointerleave', onPointerUpOrCancel);
    return () => {
      canvasEl.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUpOrCancel);
      window.removeEventListener('pointercancel', onPointerUpOrCancel);
      window.removeEventListener('pointerleave', onPointerUpOrCancel);
    };
  }, [vizScene, pcDisplay, hoverMode]);

  useEffect(() => {
    if (pcDisplay) {
      pcDisplay.setHoverActive(hoverMode);
    }
  }, [hoverMode, pcDisplay]);
  /*
   * The 'real' aspect ratio of the 2D image we compute here is the
   * horizontal fov (360°) divided by the vertical fov (different for
   * every sensor model). And for some model, this ratio is fairly smaller
   * if we always try to place resize the panel based on width and ratio
   * it will cause the image panel take too many screen area.
   * Ideally, for a single image, it shouldn't take more than 25% vertical space.
   * Hence, we will do run below function once to auto resize panel at the beginning
   */
  const TwoDImageContainerRef = useRef<HTMLDivElement>(null);
  const isRatioChecked = useRef(false);
  useEffect(() => {
    if (isRatioChecked.current) return;
    const container = TwoDImageContainerRef.current;
    if (container?.firstElementChild) {
      const child = container.firstElementChild as HTMLElement;
      const childHeight = child.offsetHeight;
      const childWidth = child.offsetWidth;
      const screenHeight = window.innerHeight;

      if (childHeight / screenHeight > 0.2 && !containerState) {
        const adjustedRatio = (screenHeight * 0.2) / childHeight;
        const adjustedWidth = Math.round(childWidth * adjustedRatio);

        setContainerState({
          top: child.offsetTop,
          left: child.offsetLeft + Math.round((childWidth - adjustedWidth) / 2),
          width: adjustedWidth,
        });
        isRatioChecked.current = true;
      }
    }
  }, [containerState, imageInfo, setContainerState]);

  useImperativeHandle(
    ref,
    () => ({
      setSeekingState: (seeking: boolean) => {
        isSeekingRef.current = seeking;
        setIsSeeking(seeking);
        if (seeking) {
          // Stop hover while seeking
          if (pcDisplay && hoverMode) {
            setHoveredPointData(null);
            pcDisplay.setHoverActive(false);
          }
        } else if (hoverMode && !isZooming) {
          // Re-enable hover when seeking stops
          if (pcDisplay) {
            pcDisplay.setHoverActive(true);
          }
        }
      },
      getIsDrawn: () => isDrawn,
    }),
    [pcDisplay, hoverMode, isZooming, isDrawn]
  );

  return (
    <Grid
      placeItems="stretch"
      gridTemplateRows="1fr"
      position="relative"
      // Prevent floating panels from bleeding outside the viz area
      overflow="hidden"
      // Tomas: [3/12/2022] Maybe a hack. This was added here explicitly since
      // windows does not stretch the grid to full width otherwise
      w="100%"
    >
      <Box
        id="container-image2d"
        ref={TwoDImageContainerRef}
        position="absolute"
        inset={0}
        pointerEvents="none"
      >
        {is2DImageVisible && selectedImageData && (
          <TwoDImagePanel
            // Use data from the selectedImageData memo
            key={selectedImageData.sensorName}
            sensorName={selectedImageData.sensorName}
            images={selectedImageData.observables}
            imageAspectRatio={selectedImageData.info.aspectRatio}
            imageDims={selectedImageData.info.dimensions}
            onClose={() => {
              setIs2DImageVisible(false); // Action to hide the panel
            }}
            flipped={flipped}
            showAngleRulers={showImageRulers}
            zoom={zoom}
            scaledOffset={scaledOffset}
            setZoom={setZoom}
            setScaledOffset={setScaledOffset}
            setContainerState={setContainerState}
            containerState={containerState}
          />
        )}
      </Box>
      <Box minH="0" flexGrow={1}>
        {isDrawn ? null : (
          <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
            <Loader label="Loading lidar viewer" />
          </Flex>
        )}
        <Box
          w="100%"
          h="100%"
          // Ref for CSS2DRenderer to render range ring labels
          position="absolute"
          ref={threeDSceneDivRef}
          pointerEvents="none"
        />
        <chakra.canvas ref={threeDSceneCanvasRef} w="100%" h="100%" />
        {hoverMode &&
          !isPointerDown &&
          !isSeeking &&
          !isZooming &&
          hoveredPointData && (
            <PointCloudHoverTooltip
              hoveredPointData={hoveredPointData}
              isVisible
            />
          )}
      </Box>
    </Grid>
  );
});
